// src/pages/SupportTicketDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { ArrowLeft, Clock, Mail, Tag, User, CheckCircle2 } from "lucide-react";

const pill = (label, cls) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs ${cls}`}>{label}</span>
);

export default function SupportTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTicket = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("support_tickets")
      .select("*")
      .eq("id", id)
      .single();
    if (!error) setTicket(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return null;
  if (!ticket) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 mb-4">
          <ArrowLeft className="w-4 h-4" /> {t("supportTicketDetail.back")}
        </button>
        <div className="p-6 border rounded-2xl">{t("supportTicketDetail.notFound")}</div>
      </div>
    );
  }

  const days = Math.floor((Date.now() - new Date(ticket.created_at)) / 86400000);
  const statusCls = {
    open: "bg-blue-100 text-blue-700",
    pending: "bg-yellow-100 text-yellow-700",
    sent: "bg-green-100 text-green-700",
    resolved: "bg-green-100 text-green-700",
    closed: "bg-gray-100 text-gray-700",
    canceled: "bg-red-100 text-red-700",
  }[ticket.status] || "bg-gray-100 text-gray-700";

  const statusLabel = t(`supportTicketDetail.status.${ticket.status}`, {
    defaultValue: String(ticket.status || "").toUpperCase(),
  });

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> {t("supportTicketDetail.back")}
        </button>
        <Link to="/team/dashboard" className="text-sm underline">
          {t("supportTicketDetail.dashboard")}
        </Link>
      </div>

      <div className="border rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-medium">{ticket.subject}</h1>
            <div className="mt-2 flex flex-wrap gap-2">
              {pill(statusLabel, statusCls)}
              {ticket.priority && pill(t("supportTicketDetail.priority", { value: ticket.priority }), "bg-gray-100 text-gray-700")}
              {ticket.channel && pill(t("supportTicketDetail.channel", { value: ticket.channel }), "bg-gray-100 text-gray-700")}
              {ticket.kind && pill(ticket.kind, "bg-gray-100 text-gray-700")}
              {ticket.estimation_mode && pill(t("supportTicketDetail.mode", { value: ticket.estimation_mode }), "bg-gray-100 text-gray-700")}
            </div>
            <div className="mt-3 text-sm text-gray-600 flex items-center gap-4 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <Clock className="w-4 h-4" /> {t("supportTicketDetail.createdAgo", { count: days })}
              </span>
              <span className="inline-flex items-center gap-1">
                <User className="w-4 h-4" /> {t("supportTicketDetail.client", { id: ticket.requester_id })}
              </span>
            </div>
          </div>
          {ticket.status !== "resolved" && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                await supabase
                  .from("support_tickets")
                  .update({ status: "resolved", resolved_at: new Date().toISOString() })
                  .eq("id", ticket.id);
                fetchTicket();
              }}
            >
              <button className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-black text-white hover:bg-gray-800">
                <CheckCircle2 className="w-4 h-4" /> {t("supportTicketDetail.markResolved")}
              </button>
            </form>
          )}
        </div>

        {ticket.message && (
          <div className="mt-6 p-4 rounded-xl bg-gray-50 border">
            <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
              <Mail className="w-4 h-4" /> {t("supportTicketDetail.message")}
            </div>
            <p className="whitespace-pre-line leading-relaxed">{ticket.message}</p>
          </div>
        )}

        {ticket.tags?.length > 0 && (
          <div className="mt-6 flex items-start gap-2">
            <Tag className="w-4 h-4 text-gray-600 mt-1" />
            <div className="flex flex-wrap gap-2">
              {ticket.tags.map((tag, index) => (
                <React.Fragment key={`${tag}-${index}`}>{pill(tag, "bg-gray-100 text-gray-700")}</React.Fragment>
              ))}
            </div>
          </div>
        )}

        {ticket.meta && Object.keys(ticket.meta || {}).length > 0 && (
          <div className="mt-6">
            <div className="text-sm text-gray-600 mb-1">{t("supportTicketDetail.metadata")}</div>
            <pre className="text-sm bg-gray-50 border rounded-xl p-3 overflow-auto">
              {JSON.stringify(ticket.meta, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
